import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { routes } from "routes";

import { api } from "api";

import { Spacer } from "components/Spacer";
import { Title } from "components/Title";
import { Description } from "components/Description";
import { Button } from "components/Button";

import { ValidationWrapper } from "../../documents";

import { useCurrentDriverApplication } from "../OnboardingBreadcrumbs";
import { useApplicationCompleted } from "./useApplicationCompleted";
import { destroySnack, errorSnack, loadingSnack, successSnack } from "helpers/message";

export function SubmitApplicationPage() {
  const { t } = useTranslation();

  const { fleetSlug } = useParams();
  const driverApplication = useCurrentDriverApplication();
  const isApplicationCompleted = useApplicationCompleted();
  const isApplicationRejected = driverApplication?.status === "rejected";

  const navigate = useNavigate();

  const [
    patchDriverApplication,
    {
      isLoading: isPatching,
      isSuccess: isPatchingSucceed,
      isError: isPatchingFailed,
      reset,
    },
  ] = api.endpoints.patchDriverApplication.useMutation();

  useEffect(() => {
    if (isPatching) {
      loadingSnack({
        content: t("processing.applicationSubmitting"),
        duration: 0,
        key: "submitting_application",
      });
    } else {
      destroySnack("submitting_application");
    }
  }, [isPatching, t]);

  useEffect(() => {
    if (isPatchingFailed) {
      errorSnack({
        content: t("error.applicationSubmittingFailed"),
      });
    }

    if (isPatchingSucceed) {
      successSnack({
        content: t("success.applicationSubmitted"),
      });
    }

    if (isPatchingSucceed || isPatchingFailed) {
      reset();
      destroySnack("submitting_application");
    }
  }, [isPatchingSucceed, isPatchingFailed, navigate, reset, t]);

  useEffect(() => {
    if (!isPatchingSucceed) return;
    navigate(routes.onboardingApplicationIsSubmitted.getURL(fleetSlug));
  }, [isPatchingSucceed, navigate, fleetSlug]);

  function onSubmit() {
    patchDriverApplication({
      driverApplicationId: driverApplication?.id,
      status: "new",
    });
  }

  if (isApplicationRejected) {
    const comment = driverApplication?.comment ?? "";

    return (
      <ValidationWrapper>
        <Title>{t("pageSubmitApplication.yourApplicationRejected")}</Title>
        <Description>
          <p>{t("pageSubmitApplication.thankYouButApplicationRejected")}</p>

          {comment.length > 0 ? (
            <p>{t("pageSubmitApplication.comment", { comment })}</p>
          ) : null}
        </Description>
      </ValidationWrapper>
    );
  }

  return (
    <ValidationWrapper>
      <Title>{t("pageSubmitApplication.submitApplication")}</Title>
      <Description>{t("pageSubmitApplication.youReceiveEmail")}</Description>
      <Spacer />

      {!isApplicationCompleted ? (
        <Description importance="error">
          {t("pageSubmitApplication.youSubmitIncompleteApplication")}
        </Description>
      ) : null}
      <Spacer />

      <Button
        block
        variant="primary"
        shape="round"
        fontWeight="bold"
        onClick={onSubmit}
      >
        {t("action.submitApplication")}
      </Button>
    </ValidationWrapper>
  );
}
